import { useMutation } from '@tanstack/react-query';
import { api } from '@/api';
import { QuoteApproveInternallyBody, QuoteStatusChangeBody } from '@/api/apis/quote';
import { Case, QuoteStatus } from '@/api/apis/case';
import { quotesQueryKeys } from '@/queries/quotes/quotes.keys';
import { refetchRouteLogistics } from '@/queries/route-planner/utils';
import { updateCaseCache } from '../case';

const checkQuoteInCase = async (caseId: string, quoteStatus?: QuoteStatus): Promise<Case> => {
  return new Promise((resolve) => {
    const intervalId = setInterval(async () => {
      const updatedCase = await api.case.getCaseById(caseId);

      if (updatedCase.quote && (!quoteStatus || updatedCase.quote.status === quoteStatus)) {
        resolve(updatedCase as Case);
        clearInterval(intervalId);
      }
    }, 1000);
  });
};

const rejectQuoteInternally = async (
  caseId: string,
  variables: Pick<QuoteStatusChangeBody, 'comment'> & { id: string },
): Promise<Case> => {
  await api.quote.rejectQuoteInternally(variables);

  return checkQuoteInCase(caseId, QuoteStatus.NoQuote);
};

const approveQuoteInternally = async (
  caseId: string,
  variables: QuoteApproveInternallyBody & { id: string },
): Promise<Case> => {
  await api.quote.approveQuoteInternally(variables);

  return checkQuoteInCase(caseId, QuoteStatus.ApprovedInternally);
};

export const useQuoteRejectInternally = () => {
  return useMutation({
    mutationFn: ({ caseId, ...variables }: { id: string; comment?: string; caseId: string }) =>
      rejectQuoteInternally(caseId, variables),
    onSuccess: async (data) => {
      await updateCaseCache(data);

      if (data.logisticsReference?.id) {
        await refetchRouteLogistics(data.logisticsReference.id);
      }
    },
  });
};

export const useQuoteApproveInternally = () => {
  return useMutation({
    mutationFn: ({ caseId, ...variables }: QuoteApproveInternallyBody & { id: string; caseId: string }) =>
      approveQuoteInternally(caseId, variables),
    meta: {
      invalidates: [quotesQueryKeys.getQuote()],
    },
    onSuccess: async (data) => {
      await updateCaseCache(data);

      if (data.logisticsReference?.id) {
        await refetchRouteLogistics(data.logisticsReference.id);
      }
    },
  });
};

export const useQuoteSendToTXCenter = () => {
  return useMutation({
    mutationFn: (variables: { id: string; comment?: string }) => api.quote.sendQuoteToTXCenter(variables),
  });
};

export const useQuoteRecall = () => {
  return useMutation({
    mutationFn: (variables: { id: string; comment: string }) => api.quote.recallQuote(variables),
    onSuccess: async (data) => {
      await updateCaseCache(data);

      if (data.logisticsReference?.id) {
        await refetchRouteLogistics(data.logisticsReference.id);
      }
    },
  });
};

export const useQuoteApproveTxCenterProvider = () => {
  return useMutation({
    mutationFn: (variables: { id: string; comment?: string }) => api.quote.quoteApproveTxCenterProvider(variables),
  });
};

export const useGetInvoice = () => {
  return useMutation({
    mutationFn: async (variables: { caseId: string; documentId: string }) => {
      const response = await api.quote.getInvoiceQuote(variables.caseId, variables.documentId);
      return new Blob([response], { type: 'application/pdf' });
    },
  });
};
